import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, Subject } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { ClientService } from '@services/client.service';
import { NotificationService } from '@services/notification.service';
import { RefDataService } from '@services/ref-data.service';
import { Brand } from '@shared/models/dataref.model';
import {
  Client,
  ClientConfiguration,
  DomainConfiguration,
  MarketClientConfiguration,
  UIDomainConfigurationWithCapacity,
} from '../models/client.model';

const RestrictedApplicationFields = [
  'apikey',
  'functionalId',
  'name',
  'nature',
  'application',
  'firebaseId',
  'roles',
  'clientId',
  'currentVersion',
  'latestVersion',
  'configurations',
  '$$hashKey',
];
const RestrictedConfigurationFields = [
  'version',
  'mustUpgrade',
  'shouldUpgrade',
  'availableDomains',
  'availableMarkets',
  'defaultMarket',
  'rcuBaseUrl',
  'rcuMarket',
  'rcuBrand',
  'markets',
  'fallback',
  'localizations',
];
const RestrictedMarketFields = [
  'name',
  'exceptions',
  'profilePerimeter',
  'availableLangs',
  'defaultAppliances',
  'availableDomains',
  'defaultLang',
  'defaultCountry',
  'domains',
  'domainsWithApplianceCapacity',
  'servicesConfs',
  'isAccountMandatory',
];

export const AllRestrictedFields = [...RestrictedApplicationFields, ...RestrictedConfigurationFields, ...RestrictedMarketFields];

@Component({
  selector: 'app-clients-form',
  templateUrl: './clients-form.component.html',
  styleUrls: ['./clients-form.component.scss'],
  standalone: false,
})
export class ClientsFormComponent implements OnInit, OnDestroy {
  form = this.formBuilder.group({});
  natureList: any[] = [];
  applicationList: any[] = [];
  roleList: any[] = [];
  brands: Brand[] = [];
  isEdit = false;
  clientFid = '';
  clientOriginal: Client | null = null;
  isSubmitted = false;

  allRestrictedFields = AllRestrictedFields;

  // subject used to unsubscribe to avoid memory leaks on component destruction
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly clientService: ClientService,
    private readonly notificationService: NotificationService,
    private readonly translateService: TranslateService,
    private readonly refDataService: RefDataService,
  ) {}

  ngOnInit(): void {
    this.initForm(null);

    // listen to route params to fetch (if needed) client from remote service
    this.route.params
      .pipe(
        switchMap((param: Params) => this.getClientFromParams(param)),
        takeUntil(this.destroy$),
      )
      .subscribe(
        (client: Client | null) => this.initComponentFromClient(client),
        (httpError: HttpErrorResponse) => {
          if (httpError.status === 404) {
            this.router.navigate(['404']);
          }
        },
      );

    this.refDataService
      .getByKey('applications', true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((allApplications) => {
        this.applicationList = this.refDataService.translateReference(this.refDataService.toArray(allApplications));
      });

    this.refDataService
      .getByKey('clientNatures', true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((allNatures) => {
        this.natureList = this.refDataService.translateReference(this.refDataService.toArray(allNatures));
      });

    this.refDataService
      .getByKey('clientRoles', true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((allRoles) => {
        this.roleList = this.refDataService
          .translateReference(this.refDataService.toArray(allRoles))
          .map((role: any) => ({ key: role.key, translatedName: role.translatedName }));
      });

    this.refDataService
      .getByKey('brands', true)
      .pipe(
        tap((allBrands) => (this.brands = this.refDataService.translateReference(this.refDataService.toArray(allBrands)))),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  /**
   * Init component data related to the client to display.
   * Client may be null which indicate it's a new one to create
   */
  initComponentFromClient(client: Client | null): void {
    if (client != null) {
      this.clientFid = client.functionalId;
      this.isEdit = true;
      this.clientOriginal = client;
      this.initForm(client);
    } else {
      this.clientFid = '';
      this.isEdit = false;
    }
  }

  /**
   * Checks inside the params the clientId. Returns asynchronously null or the client from a remote service if there is a clientId.
   * May throw an exception if there remote service do not respond as expected
   *
   * @param params route parameters
   */
  getClientFromParams(params: Params): Observable<Client | null> {
    if (!params.clientFid) {
      return of(null);
    }

    return this.clientService.getClient(params.clientFid).pipe(
      tap((client) => this.setHelperFieldsForDisplayPurpose(client)),
      takeUntil(this.destroy$),
    );
  }

  resetForm(): void {
    this.initForm(this.clientOriginal);
  }

  save(): void {
    if (this.form.invalid || this.isSubmitted) {
      return;
    }

    this.isSubmitted = true;
    let client: Client = {
      ...(Object.assign({}, this.clientOriginal) as any), // merge object (always first for overwrite object)
      apikey: this.form.controls.apikey.value,
      name: this.form.controls.name.value,
      nature: this.form.controls.nature.value,
      application: this.form.controls.application.value,
      brand: this.form.controls.brand.value,
      firebaseId: this.form.controls.firebaseId.value,
      clientId: this.form.controls.clientId.value === '' ? null : this.form.controls.clientId.value,
      roles: this.form.controls.roles.value,
      configurations: JSON.parse(JSON.stringify(this.form.controls.configurations.value)), // deep clone
      latestVersion: this.form.controls.latestVersion.value,
      currentVersion: this.form.controls.currentVersion.value,
      apimUrl: this.form.controls.apimUrl.value,
      apimSubscriptionKey: this.form.controls.apimSubscriptionKey.value,
      apimAltSubscriptionKey: this.form.controls.apimAltSubscriptionKey.value,
    };
    client = this.formatClient(client);

    if (this.isEdit) {
      this.clientService
        .updateClient(client, this.clientFid)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (clientUpdated) => {
            this.isSubmitted = false;
            this.clientOriginal = clientUpdated;
            this.notificationService.sendSuccess(this.translateService.instant('APPLICATIONS.MSG.SUCCESS.UPDATE'));
          },
          (err) => this.errorManager(err),
        );
    } else {
      this.clientService
        .createClient(client)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (clientCreated) => {
            this.isSubmitted = false;
            this.notificationService.sendSuccess(this.translateService.instant('APPLICATIONS.MSG.SUCCESS.UPDATE'));
            this.router.navigateByUrl('clients/edit/' + clientCreated.functionalId);
          },
          (err) => this.errorManager(err),
        );
    }
  }

  errorManager(err: HttpErrorResponse): void {
    let errorText = null;
    if (err.status && err.status === 403) {
      errorText = this.translateService.instant('RECIPE_UPDATE_FORBIDDEN');
      this.notificationService.sendError(errorText);
    } else {
      this.notificationService.sendAPIError(err);
    }
    this.isSubmitted = false;
  }

  notifyCopy($event: boolean): void {
    this.notificationService.sendSuccess(this.translateService.instant('APPLICATIONS.MSG.SUCCESS.COPIED'));
  }

  formatClient(client: Client): Client {
    if (client.configurations && client.configurations[0]) {
      client.configurations.forEach((conf: ClientConfiguration) => {
        // copy new market field
        let allMarketField: any = {};
        conf.markets?.forEach((market: any) => {
          // put all in big object
          allMarketField = { ...allMarketField, ...market };
        });
        const marketField: any = {};
        Object.keys(allMarketField).forEach((key: any) => {
          // remove used fields
          if (!this.allRestrictedFields.includes(key)) {
            marketField[key] = allMarketField[key];
          }
        });
        Object.keys(marketField).forEach((fieldKey: any) => {
          // init fields
          conf.markets.forEach((market: any) => {
            if (!market[fieldKey] && market[fieldKey] !== false) {
              switch (this.typeOf(marketField[fieldKey])) {
              case 'list':
                market[fieldKey] = [];
                break;
              case 'boolean':
                market[fieldKey] = false;
                break;
              case 'number':
                market[fieldKey] = 0;
                break;
              default:
                market[fieldKey] = '';
              }
            }
          });
        });
        // clean market.domains
        conf.markets?.forEach((market: MarketClientConfiguration) => {
          market.availableDomains = [];
          market.domains = [];

          // build the two array properties available domains and domains from the single source of truth domainConfigurationWithCapacity
          // it allows us to be sure that the two properties are always sync
          (market.domainsWithApplianceCapacity ?? []).forEach((domainConfigurationWithCapacity: UIDomainConfigurationWithCapacity) => {
            if (domainConfigurationWithCapacity.isActive && domainConfigurationWithCapacity.defaultApplianceId) {
              market.availableDomains.push({
                domain: domainConfigurationWithCapacity.domainKey,
                applianceId: domainConfigurationWithCapacity.defaultApplianceId,
                capacity: domainConfigurationWithCapacity.capacity,
              });
              market.domains.push({
                domainKey: domainConfigurationWithCapacity.domainKey,
                defaultApplianceId: domainConfigurationWithCapacity.defaultApplianceId,
                ugcEnabled: domainConfigurationWithCapacity.isUgcEnabled,
                personalizedAdaptationEnabled: domainConfigurationWithCapacity.isPersonalizedAdaptationEnabled,
                order: domainConfigurationWithCapacity.order,
              });
            }
          });

          delete market.defaultAppliances;
          delete market.domainsWithApplianceCapacity;
        });
      });
      // find last version
      let lastV = client.configurations[0].version;
      client.configurations.forEach((conf: any) => {
        if (conf.version > lastV) {
          lastV = conf.version;
        }
      });
      client.latestVersion = lastV;
      if (client.currentVersion === null) {
        client.currentVersion = client.latestVersion;
      }
    } else {
      delete client.latestVersion;
      delete client.currentVersion;
    }
    if (this.form.controls.apikey.value === '') {
      delete client.apikey;
    }
    if (client.application === null) {
      delete client.application;
    }
    return client;
  }

  typeOf(obj: unknown): string {
    if (obj === null) {
      return 'number';
    }
    return typeof obj === 'object' ? 'list' : typeof obj;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // iterate over markets configurations to initialize the domainsConfigurationWithcapactity property which is used in the form afterward
  private setHelperFieldsForDisplayPurpose(client: Client): void {
    client?.configurations?.forEach((globalConfiguration) => {
      if (!globalConfiguration.availableDomains) {
        globalConfiguration.availableDomains = [];
      }

      globalConfiguration.markets?.forEach((marketConfiguration) => {
        if (!marketConfiguration.domains) {
          marketConfiguration.domains = [];
        }

        // sort activated domains by order
        this.sortActivatedDomainsByOrder(marketConfiguration.domains);

        // init activated domains that does not have order with the his index in the list
        this.initOrderActivatedDomains(marketConfiguration.domains);
        const domainsWithApplianceCapacity: UIDomainConfigurationWithCapacity[] = marketConfiguration.domains.map(
          (currentDomainConfiguration) => ({
            domainKey: currentDomainConfiguration.domainKey,
            defaultApplianceId: currentDomainConfiguration?.defaultApplianceId,
            isPersonalizedAdaptationEnabled: currentDomainConfiguration?.personalizedAdaptationEnabled || false,
            isUgcEnabled: currentDomainConfiguration?.ugcEnabled || false,
            isActive: true,
            order: currentDomainConfiguration.order,
          }),
        );

        if (!marketConfiguration.availableDomains) {
          marketConfiguration.availableDomains = [];
        }

        marketConfiguration.availableDomains.forEach((availableDomain) => {
          const foundDomainWithApplianceCapacity = domainsWithApplianceCapacity.find(
            (domainWithApplianceCapacity) => domainWithApplianceCapacity.domainKey === availableDomain.domain,
          );

          if (foundDomainWithApplianceCapacity) {
            foundDomainWithApplianceCapacity.capacity = availableDomain.capacity;
          } else {
            domainsWithApplianceCapacity.push({
              domainKey: availableDomain.domain,
              defaultApplianceId: availableDomain.applianceId,
              capacity: availableDomain.capacity,
              isPersonalizedAdaptationEnabled: false,
              isUgcEnabled: false,
              isActive: true,
              order: availableDomain.order,
            });
          }
        });

        marketConfiguration.domainsWithApplianceCapacity = domainsWithApplianceCapacity;
      });
    });
  }

  private sortActivatedDomainsByOrder(domains: DomainConfiguration[]): void {
    domains.sort((left, right) => {
      if (typeof left.order === 'number' && typeof right.order === 'number') {
        return left.order - right.order;
      } else if (typeof right.order === 'number') {
        return 1;
      } else if (typeof left.order === 'number') {
        return -1;
      }
      return 0;
    });
  }

  private initOrderActivatedDomains(domains: DomainConfiguration[]): void {
    domains.forEach((currentDomainConfiguration, index) => {
      currentDomainConfiguration.order = index;
    });
  }

  private initForm(client: Client | null): void {
    this.form = this.formBuilder.group({
      apikey: new UntypedFormControl(client ? { value: client.apikey, disabled: this.isEdit } : ''),
      name: [client?.name ?? '', Validators.required],
      nature: new UntypedFormControl(
        client
          ? {
            value: client.nature,
            disabled: this.isEdit,
          }
          : '',
        Validators.required,
      ),
      application: [client?.application ?? ''],
      brand: [client?.brand ?? ''],
      firebaseId: [client?.firebaseId ?? ''],
      clientId: [client?.clientId ?? ''],
      roles: [client?.roles ?? []],
      currentVersion: [client ? client.currentVersion : null],
      latestVersion: [client ? client.latestVersion : null],
      apimUrl: [client ? client.apimUrl : null],
      apimSubscriptionKey: [client ? client.apimSubscriptionKey : null],
      apimAltSubscriptionKey: [client ? client.apimAltSubscriptionKey : null],
      configurations: [client ? client.configurations : null],
    });
  }
}
